import Analytics from '@withlocals/withlocals-analytics';
import { Translator } from 'Translator';
import { ClosestArea } from 'components/wrappers/withLocation';
import isEqual from 'lodash.isequal';
import { Observable } from 'rxjs';
import { WlWindow } from 'typings/globals/cordova';
import { calculateDistance } from 'utils/LocationHelper';
import { isCordova } from 'utils/platform';
import { mapObservable } from 'utils/rxs';

import {
  LocationDataSource,
  LocationSourceQuality,
} from '../../../cordovaPlugins/Location';
import { appServices } from '../../../singletons';
import {
  requestHighAccuracy,
  requestPermissions,
  waitForGps,
} from './WithPermissionRequesting';

declare const window: WlWindow;

const showAlert = isCordova()
  ? window.navigator &&
    window.navigator.notification &&
    window.navigator.notification.alert
  : (text, _, title) => alert(`${title} ${text}`);

function sorryNothingFound() {
  Analytics.track('SearchNearby', 'Nothing found');
  return new Promise((resolve) => {
    showAlert(
      Translator.translate(
        'Search.SearchNearby.NoExperiencesFoundMessage',
        {},
        true,
        'But luckily we are available at many other beautiful travel destinations.',
      ), // message
      resolve,
      Translator.translate(
        'Search.SearchNearby.NoExperiencesFoundTitle',
        {},
        true,
        'No Withlocals experiences found near you.',
      ),
      ['close'], // buttonLabels
    );
  });
}

function sorryNotPermitted() {
  Analytics.track('SearchNearby', 'Not permitted');
  return new Promise((resolve) => {
    showAlert(
      Translator.translate(
        'Search.SearchNearby.NotPermittedMessage',
        {},
        true,
        'In order to find nearby destination please allow location tracking.',
      ), // message
      resolve,
      Translator.translate(
        'Search.SearchNearby.NotPermittedTitle',
        {},
        true,
        'Location tracking not enabled on your device.',
      ),
      ['close'], // buttonLabels
    );
  });
}

function sorryError() {
  Analytics.track('SearchNearby', 'Something went wrong');
  return new Promise((resolve) => {
    showAlert(
      Translator.translate(
        'Search.SearchNearby.SomethingWentWrongMessage',
        {},
        true,
        'But luckily we are available at many other beautiful travel destinations.',
      ),
      resolve,
      Translator.translate(
        'Search.SearchNearby.SomethingWentWrongTitle',
        {},
        true,
        'Something has gone wrong.',
      ),
      ['close'], // buttonLabels
    );
  });
}

const findClosestCities = ({ longitude, latitude }) => {
  return fetch(
    `${URL_API_HOST}/api/v1/geocode/closest-cities/${latitude}/${longitude}/100`,
  ).then((r) => r.json()) as Promise<ClosestArea[]>;
};

const findClosestCitiesByIP = () => {
  return fetch(`${URL_API_HOST}/api/v1/geocode/closest-cities`).then((r) =>
    r.json(),
  ) as Promise<ClosestArea[]>;
};

const fetchCities = ({
  latitude = 0,
  longitude = 0,
}): Promise<ClosestArea[]> => {
  return !!latitude && !!longitude
    ? findClosestCities({ latitude, longitude })
    : findClosestCitiesByIP();
};

// export const closestCities$ : { loading:boolean, fromCache:boolean, source:'gps|ip', cities:[] }
interface ClosestCities {
  cities?: ClosestArea[];
  loading: boolean;
  fromCache: boolean;
  source?: 'GPS' | 'IP';
  quality?: 'STREET' | 'CITY' | 'COUNTRY';
}

export const closestCities$: Observable<ClosestCities> = appServices.location$
  .do((l) => console.debug('withClosestWlCity.location', l))
  .distinctUntilChanged((x, y) => {
    if (!x || isEqual(x, y)) {
      return true;
    }
    if (x.quality !== y.quality) {
      return false;
    }
    return calculateDistance(x, y) < 5;
  })
  .do((l) => console.debug('withClosestWlCity.going to fetch', l.location))
  .mergeMap(({ source, quality, location }) =>
    fetchCities(location).then((cities) => ({
      source,
      quality,
      cities: cities.filter(
        (city) => city.location.area === city.location.city,
      ), // filter small cities out
    })),
  )
  .map((state) => ({ ...state, loading: false, fromCache: false }))
  .startWith<ClosestCities>({ loading: true, fromCache: false })
  .distinctUntilChanged(isEqual)
  .do((l) => console.debug('withClosestWlCity.result', l))
  .shareReplay(1);

interface ClosestCity {
  city?: ClosestArea;
  loading: boolean;
  fromCache: boolean;
  source?: LocationDataSource;
  quality?: LocationSourceQuality;
}

export const closestCity$: Observable<ClosestCity> = closestCities$.map(
  (state) => {
    if (state.loading) {
      return { loading: true, fromCache: false };
    }
    return {
      loading: false,
      fromCache: false,
      city: state.cities[0],
      source: state.source,
      quality: state.quality,
    };
  },
);

export interface AddSearchNearbyPropsOuter {
  onSelect: (country: string, area: string) => Promise<any> | any;
}

export interface AddSearchNearbyPropsInner {
  hideSearchNearby: boolean;
  closestCitiesLoading: boolean;
  findNearBy: () => Observable<
    FindNearbyResult<{ loading: boolean; failure?: string }>
  >;
  trackingType: string;
  cities: ClosestArea[];
}

const shouldRequestMorePermissions$: Observable<boolean> =
  appServices.permissions$
    .map(
      (p) =>
        p.location.canRequestBackground ||
        p.location.canRequestInUse ||
        p.location.canReRequest,
    )
    .startWith(true)
    .shareReplay(1);

const hasGps$: Observable<boolean> = appServices.permissions$
  .map((p) => p.location.inUse || p.location.background)
  .startWith(true)
  .shareReplay(1);

const hasHighAccuracyPermissions$: Observable<boolean> =
  appServices.permissions$
    .map((p) => p.location.getHighAccuracyPermission)
    .startWith(false)
    .shareReplay(1);

export interface FindNearbyResult<T> {
  t: T;
  loading: boolean;
}

class NotPermittedError extends Error {
  message = 'Has no permission!';
}

function findNearByFactory<T extends {}>(
  onSelect: (
    country: string,
    area: string,
    distanceTo: number,
  ) => Promise<T> | T,
): () => Observable<
  FindNearbyResult<T & { loading: boolean; failure?: string }>
> {
  return () => {
    return Observable.combineLatest(
      shouldRequestMorePermissions$,
      hasHighAccuracyPermissions$,
      hasGps$,
      closestCity$,
    )
      .take(1)
      .do((s) => console.log('findNearByFactory', s))
      .mergeMap(([shouldRequestMore, hasHighAccuracy, hasGps, closestCity]) => {
        if (
          shouldRequestMore ||
          hasGps ||
          !hasHighAccuracy ||
          (closestCity.city && closestCity.source === 'IP')
        ) {
          return requestPermissions()
            .map((hasPermission) => {
              if (!hasPermission) {
                throw new NotPermittedError();
              }
              return hasPermission;
            })
            .switchMap(() => {
              if (!hasHighAccuracy) {
                return requestHighAccuracy();
              }
              return Promise.resolve(true);
            })
            .switchMap(() => waitForGps())
            .switchMap(() =>
              closestCity$.filter((l) => l.source === 'GPS').take(1),
            )
            .switchMap((c) => {
              if (c && c.city && c.city.location) {
                Analytics.track('SearchNearby', 'City found', { city: c });
                // ensure its in a promise
                return Promise.resolve(
                  onSelect(
                    c.city.location.country.toLowerCase().replace(/\s/g, '-'),
                    c.city.location.area.toLowerCase().replace(/\s/g, '-'),
                    c.city.distanceTo,
                  ),
                );
              }
              // return sorryNothingFound();
              return Observable.throw('no cities close');
            })
            .catch((e) => {
              console.log('error', e, 'Reloading closest cities failed');
              const handle =
                e instanceof NotPermittedError
                  ? sorryNotPermitted
                  : sorryNothingFound;
              return Observable.throw({
                handle,
                failure: `Reloading closest cities failed, most likely due to timeout (or permission disallowance): ${e}`,
              });
            })
            .toPromise();
        }
        if (closestCity.city) {
          return Promise.resolve(
            onSelect(
              closestCity.city.location.country
                .toLowerCase()
                .replace(/\s/g, '-'),
              closestCity.city.location.area.toLowerCase().replace(/\s/g, '-'),
              closestCity.city.distanceTo,
            ),
          );
        }
        return Promise.resolve(null);
      })
      .catch((e) => {
        if (e.handle) {
          return Observable.throw(e);
        }
        return Observable.throw({ handle: sorryError, failure: e });
      })
      .map((state) => {
        return { ...state, faiure: null, loading: false };
      })
      .startWith({ loading: true })
      .do((s) => console.log('findNearByFactory', s));
  };
}

const showSearchNearby$ = appServices.permissions$
  .map(
    ({ location }) =>
      location.canRequestInUse ||
      location.inUse ||
      location.canRequestBackground ||
      location.canReRequest,
  )
  .shareReplay(1);

showSearchNearby$.subscribe((showSearchNearby) =>
  console.log('showSearchNearby', showSearchNearby),
);

export const addSearchNearbyProps = mapObservable<
  AddSearchNearbyPropsOuter,
  AddSearchNearbyPropsOuter,
  AddSearchNearbyPropsInner
>(
  ({ onSelect }) => ({ onSelect }),
  ({ onSelect }) =>
    Observable.combineLatest(showSearchNearby$, closestCities$)
      .map(([showSearchNearby, { cities = [], loading, source }]) => ({
        cities,
        hideSearchNearby: !showSearchNearby,
        findNearBy: findNearByFactory(onSelect),
        closestCitiesLoading: loading,
        trackingType: source,
      }))
      .startWith({
        hideSearchNearby: false,
        closestCitiesLoading: false,
        findNearBy: null,
        cities: null,
        trackingType: null,
      })
      .do((p) => console.log('addSearchNearbyProps', p)),
);
